import { createSelector } from '@reduxjs/toolkit';
import type {
  AmountsByTypeRow,
  LoanAmountItem,
  LoanAmountsState,
  AmountsTotalsByType,
} from './types';

interface Store {
  loans: {
    amounts: LoanAmountsState;
  };
}

export const selectLoanAmounts = (store: Store): LoanAmountItem[] =>
  store.loans.amounts.amounts ?? [];
export const selectAmountsStatus = (store: Store) => store.loans.amounts.status;
export const selectUpdateAmountStatus = (store: Store) => store.loans.amounts.updateStatus;
export const selectDeleteAmountStatus = (store: Store) => store.loans.amounts.deleteStatus;

export const selectAmountsTotal = createSelector(selectLoanAmounts, (list: LoanAmountItem[]) =>
  list?.reduce((acc, entry) => {
    // sum quote amounts (type=1) as total USD-equivalent value
    const value = entry.type === 1 ? entry.amount : 0;
    return acc + value;
  }, 0),
);

export const selectAmountsByCurrency = createSelector(
  selectLoanAmounts,
  (list: LoanAmountItem[]) => {
    return (list || []).reduce<Record<string, number>>((acc, item) => {
      const cur = item.currency || 'UNKNOWN';
      acc[cur] =
        (acc[cur] || 0) +
        (typeof item.amount === 'number' ? item.amount : Number(item.amount) || 0);
      return acc;
    }, {});
  },
);

/**
 * Sums amount*price for base items (type=0).
 */
export const selectAmountsTotalBase = createSelector(
  selectLoanAmounts,
  (list: LoanAmountItem[]): number => {
    let base = 0;
    for (const item of list ?? []) {
      if (item.type === 0) {
        const value =
          (typeof item.amount === 'number' ? item.amount : Number(item.amount) || 0) *
          (typeof item.price === 'number' ? item.price : Number(item.price) || 0);
        base += value;
      }
    }
    return base;
  },
);

/**
 * Sums amount*price for quote items (type=1).
 */
export const selectAmountsTotalQuote = createSelector(
  selectLoanAmounts,
  (list: LoanAmountItem[]): number => {
    let quote = 0;
    for (const item of list ?? []) {
      if (item.type === 1) {
        const value =
          (typeof item.amount === 'number' ? item.amount : Number(item.amount) || 0) *
          (typeof item.price === 'number' ? item.price : Number(item.price) || 0);
        quote += value;
      }
    }
    return quote;
  },
);

/**
 * Aggregates amounts split by type:
 * type=0 → Base, type=1 → Quote.
 * Currency is case-sensitive as per source data.
 */
export const selectAmountsByTypeAndCurrency = createSelector(
  selectLoanAmounts,
  (list: LoanAmountItem[]): AmountsByTypeRow[] => {
    const map: Record<string, AmountsByTypeRow> = {};

    for (const item of list || []) {
      const cur = item.currency || 'UNKNOWN';
      const amount = typeof item.amount === 'number' ? item.amount : Number(item.amount) || 0;

      if (!map[cur]) {
        map[cur] = { currency: cur, base: 0, quote: 0 };
      }

      if (item.type === 0) {
        map[cur].base += amount;
      } else if (item.type === 1) {
        map[cur].quote += amount;
      }
    }

    return Object.values(map);
  },
);

/**
 * Combines base and quote totals. Provides backwards compatibility for existing code.
 */
export const selectAmountsTotalsByType = createSelector(
  selectAmountsTotalBase,
  selectAmountsTotalQuote,
  (base, quote): AmountsTotalsByType => ({ base, quote }),
);
